import { filterSchedules, getUserEmailId } from "@/features/calendar/utils";
import {
  getFirstDateOfWeek,
  getLastDateOfWeek,
} from "@/features/calendar/utils/dateUtils";
import { BookFollowUpModal } from "@/features/modals/bookFollowUpModal/BookFollowUpModal";
import { useModals } from "@/features/modals/hooks";
import { ScheduleActivityModal } from "@/features/modals/scheduleActivityModal/ScheduleActivityModal";
import { useReadParamsFromUrl } from "@/hooks/useReadParamsFromUrl";
import { useTitle } from "@/hooks/useTitle";
import { useWriteParamsToUrl } from "@/hooks/useWriteParamsToUrl";
import styled from "@emotion/styled";
import {
  type Appointment,
  type Customer,
  SHIFT_TYPES,
  isTask,
  useOpportunityAppointments,
  useSchedules,
} from "@repo/api";
import { useCalendarFilters, useCalendarSettings } from "@repo/api";
import {
  InlineMessage,
  LightMode,
  LoadingOverlay,
  Variables,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  PaddedContainer,
  SectionContainer,
  useCustomer,
  useSettings,
} from "@repo/ui";
import { type RecursivePartial, useTypesafeActions } from "@repo/utils";
import { useEffect, useState } from "react";

import {
  clearFollowUpProjectContext,
  getFollowUpProjectContext,
} from "../appointments/followUpProjectContext";
import { isAppointment, isOpenSlot } from "../appointments/utils";
import { CalendarProvider } from "./CalendarProvider";
import { AgendaView } from "./agenda-calendar/AgendaView";
import { CalendarFilter } from "./calendarFilter/CalendarFilter";
import {
  type AppointmentFilterActions,
  AppointmentFilterDefault,
  type CalenderAppointmentsSearchFilters,
  appointmentFilterActions,
  appointmentFilterReducer,
} from "./calendarFilter/appointmentFilterReducer";
import {
  selectEmail,
  selectTeam,
} from "./calendarFilter/appointmentFilterSelector";
import { CalendarSecondLevelHeader } from "./components/CalendarSecondLevelHeader";
import { FollowUpBanner } from "./components/FollowUpBanner";
import { useCalendarAppointments } from "./hooks/useCalendarAppointments";
import { useIsScheduleEnabled } from "./hooks/useIsScheduleEnabled";
import { useMakeUrlParams } from "./hooks/useMakeUrlParams";
import { useSaveCalendarChanges } from "./hooks/useSaveCalendarChanges";
import { useSetDefaultCoWorkerAndTeam } from "./hooks/useSetDefaultCoWorkerAndTeam";
import {
  useSyncLoadedCalendarFilters,
  useSyncLoadedCalendarSettings,
} from "./hooks/useSyncLoadedCalendarSettings";
import {
  type OpenModalProps,
  openCalendarModalCreator,
} from "./openCalendarModal";
import { EnabledServicesModal } from "./team-calendar/EnabledServicesModal";
import { TeamCalendar } from "./team-calendar/TeamCalendar";
import { type CoWorkerScheduleActivity } from "./types";
import { useUserCalendarEvents } from "./useUserCalendar";
import { getFieldsFromUrl } from "./utils/getFieldsFromUrl";
import { WeekCalendar } from "./week-calendar/WeekCalendar";

const CalendarPageContainer = styled.div`
  padding-bottom: 20px;

  @media (max-width: ${Variables.breakpointM}) {
    padding-bottom: 60px;
  }
`;

export const CalendarLayout = () => {
  useTitle("Calendar");
  const { pushModal, hideModal } = useModals();
  const { isLoadingSettings, boka_config, calendar } = useSettings();
  const { data: calendarSettings, isLoading: isLoadingCalendarSettings } =
    useCalendarSettings();
  const { data: calendarFilters, isLoading: isLoadingCalendarFilters } =
    useCalendarFilters();

  const isFollowUpEnabled = Boolean(
    boka_config?.enable_boka_follow_up_integration
  );

  const isUnassignedOpenSlotsEnabled = Boolean(
    calendar?.enable_unassigned_open_slots
  );

  const defaultValues: any = // Trust us on this TS
    useReadParamsFromUrl<RecursivePartial<CalenderAppointmentsSearchFilters>>(
      getFieldsFromUrl
    );

  const [appointmentFilters, actions] = useTypesafeActions<
    CalenderAppointmentsSearchFilters,
    AppointmentFilterActions
  >(
    appointmentFilterReducer,
    {
      ...AppointmentFilterDefault,
      ...defaultValues,
      calendarFilters,
      calendarSettings,
      teamCalendar: (() => {
        const urlSelection = defaultValues?.teamCalendar?.selection;
        const teamId =
          (urlSelection?.kind === "team" ? urlSelection.teamId : undefined) ??
          calendarSettings.selectedTeam;

        if (teamId) {
          return {
            selection: {
              kind: "team" as const,
              teamId,
              assignedToTeam: urlSelection?.assignedToTeam ?? false,
              deselectedMemberIds:
                calendarSettings.deselectedMembersByTeam?.[teamId] ?? [],
            },
            emailId: [],
          };
        }
        return defaultValues?.teamCalendar ?? { emailId: [] };
      })(),
    },
    appointmentFilterActions
  );

  useSaveCalendarChanges(appointmentFilters.pendingSave);

  useSetDefaultCoWorkerAndTeam(
    appointmentFilters,
    actions,
    isLoadingCalendarSettings
  );

  useSyncLoadedCalendarSettings({
    calendarSettings,
    isLoadingCalendarSettings,
    defaultValues,
    actions,
  });

  useSyncLoadedCalendarFilters({
    calendarFilters,
    isLoadingCalendarFilters,
    actions,
  });

  const urlParams: Record<string, any> = useMakeUrlParams(appointmentFilters);
  useWriteParamsToUrl(
    urlParams,
    true,
    !isLoadingCalendarSettings && !isLoadingCalendarFilters
  );

  const shouldSearch = !isLoadingSettings;

  const {
    items,
    isLoadingAppointments,
    isLoadingTasks,
    isSearchingBeforeAllowedDate,
    size,
    totalSize,
    setSize,
    mutateSearchAppointments,
    openSlotItems,
    isLoadingOpenSlots,
  } = useCalendarAppointments(
    shouldSearch,
    appointmentFilters,
    undefined,
    shouldSearch &&
      appointmentFilters.tableView === "TEAM" &&
      isUnassignedOpenSlotsEnabled
  );

  const isScheduleEnabled = useIsScheduleEnabled(
    appointmentFilters.selectedDate,
    selectTeam(appointmentFilters)
  );

  const { data: outlookEvents, isValidating } =
    useUserCalendarEvents(appointmentFilters);
  const isWeekView = appointmentFilters.tableView === "OVERVIEW";
  const scheduleFromDate = isWeekView
    ? getFirstDateOfWeek(
        appointmentFilters.selectedDate,
        appointmentFilters.calendarSettings.firstDayOfWeek
      )
    : appointmentFilters.selectedDate;
  const scheduleToDate = isWeekView
    ? getLastDateOfWeek(
        appointmentFilters.selectedDate,
        appointmentFilters.calendarSettings.firstDayOfWeek
      )
    : appointmentFilters.selectedDate;

  const { data: schedules } = useSchedules({
    teamId:
      isScheduleEnabled && appointmentFilters.tableView === "TEAM"
        ? selectTeam(appointmentFilters)
        : undefined,
    emailId: getUserEmailId(appointmentFilters),
    fromDate: scheduleFromDate,
    toDate: scheduleToDate,
    enabled: isScheduleEnabled,
  });

  const [scheduledItem, setScheduledItem] = useState<
    CoWorkerScheduleActivity | undefined
  >(undefined);

  const [followUpData, setFollowUpData] = useState<
    { appointment: Appointment; customer?: Customer } | undefined
  >(undefined);

  const [followUpSlot, setFollowUpSlot] = useState<
    | {
        followUpAppointmentId?: number;
        startTime?: Date;
        endTime?: Date;
        selectedCoworker?: string;
      }
    | undefined
  >(undefined);
  const [followUpProjectContext, setFollowUpProjectContextState] = useState(
    () => getFollowUpProjectContext()
  );

  const { data: contextProjectAppointments } = useOpportunityAppointments(
    followUpProjectContext?.projectId
  );
  const { customer: contextProjectCustomer } = useCustomer(
    followUpProjectContext?.projectId
  );

  const cancelFollowUpFlow = () => {
    setFollowUpSlot(undefined);
    setFollowUpData(undefined);
    clearFollowUpProjectContext();
    setFollowUpProjectContextState(undefined);
  };

  useEffect(() => {
    if (followUpProjectContext?.appointmentId === undefined) {
      return;
    }

    const sourceAppointment = contextProjectAppointments?.find(
      (appointment) =>
        appointment.appointmentId === followUpProjectContext.appointmentId
    );

    if (!sourceAppointment) {
      return;
    }

    setFollowUpData((current) => {
      if (!current) {
        return {
          appointment: sourceAppointment,
          customer: contextProjectCustomer,
        };
      }
      if (!current.customer && contextProjectCustomer) {
        return { ...current, customer: contextProjectCustomer };
      }
      return current;
    });
  }, [
    contextProjectAppointments,
    contextProjectCustomer,
    followUpProjectContext,
  ]);

  const allCalendarItems = [...items, ...openSlotItems];

  const baseOpenModal = openCalendarModalCreator(
    allCalendarItems,
    pushModal,
    selectEmail(appointmentFilters),
    mutateSearchAppointments,
    isFollowUpEnabled
      ? (appointment, customer) => {
          setFollowUpSlot(undefined);
          setFollowUpData({ appointment, customer });
          hideModal("CALENDAR_APPOINTMENT");
        }
      : undefined
  );

  const openModal = (modalProps: OpenModalProps) => {
    if (followUpData) {
      // During the follow-up flow, only open slots can be selected to book.
      if (modalProps.type === "appointment") {
        const item = modalProps.appointmentId
          ? allCalendarItems.find(
              (i) =>
                (isTask(i) ? i.appointmentNumber : i.appointmentId) ===
                modalProps.appointmentId
            )
          : allCalendarItems.find((i) => i.id === modalProps.id);

        if (
          item &&
          isAppointment(item) &&
          isOpenSlot(item.reasonCode, item.orderNumber, item.type)
        ) {
          setFollowUpSlot({
            followUpAppointmentId: item.appointmentId,
            startTime: item.timeWindowStartTime
              ? new Date(item.timeWindowStartTime)
              : undefined,
            endTime: item.timeWindowEndTime
              ? new Date(item.timeWindowEndTime)
              : undefined,
            selectedCoworker: item.assignedCoworker,
          });
        }
      }
      // Ignore clicks on non-open-slot items while booking a follow-up.
      return;
    }

    if (modalProps.type === "scheduled_activity") {
      setScheduledItem(modalProps.item);
    } else {
      baseOpenModal(modalProps);
    }
  };

  const isLoading =
    isLoadingAppointments ||
    isLoadingTasks ||
    isLoadingOpenSlots ||
    isValidating;

  const filteredSchedules = filterSchedules({
    schedules: schedules,
    calendar: calendar,
    calendarFilters: calendarFilters,
    appointmentFilters: appointmentFilters,
  });

  return (
    <CalendarPageContainer>
      <CalendarProvider
        items={allCalendarItems}
        appointmentFilters={appointmentFilters}
        highlightedItemId={
          followUpData && followUpProjectContext?.appointmentId !== undefined
            ? String(followUpProjectContext.appointmentId)
            : undefined
        }
      >
        <CalendarSecondLevelHeader
          activeTabPanelId={
            appointmentFilters.tableView ??
            calendarSettings.preferredCalendarView ??
            "AGENDA"
          }
          onClick={(id) => actions.setTableView(id)}
        />
        <PaddedContainer
          largePaddingTop={Variables.space75}
          backgroundColor={LightMode.colourStaticWhite}
        >
          <CalendarFilter
            filters={appointmentFilters}
            actions={actions}
            onUpdateTeams={() => mutateSearchAppointments()}
            mutateSearchAppointments={mutateSearchAppointments}
            availableScheduleShiftTypes={
              isScheduleEnabled ? [...SHIFT_TYPES] : []
            }
          />
          {followUpData && (
            <FollowUpBanner
              customer={followUpData.customer}
              onCancel={cancelFollowUpFlow}
            />
          )}
        </PaddedContainer>
        <PaddedContainer largePaddingTop={Variables.space75}>
          <SectionContainer largeGap={Variables.space75}>
            {isSearchingBeforeAllowedDate && (
              <InlineMessage
                title={t("prompt.no_appointments")}
                body={t("prompt.invalid_appointment_search_period")}
                variant="cautionary"
              />
            )}
            {isLoading ? (
              <LoadingOverlay text={t("prompt.loading")} />
            ) : (
              <>
                {appointmentFilters.tableView === "TEAM" && (
                  <TeamCalendar
                    actions={actions}
                    items={items}
                    openSlotItems={openSlotItems}
                    schedules={filteredSchedules}
                    allSchedules={schedules}
                    openModal={openModal}
                    filters={appointmentFilters}
                    mutateSearchAppointments={mutateSearchAppointments}
                  />
                )}
                {appointmentFilters.tableView === "AGENDA" && (
                  <AgendaView
                    items={items}
                    outlook={outlookEvents ?? []}
                    schedules={filteredSchedules}
                    size={size}
                    totalSize={totalSize}
                    setSize={setSize}
                    openModal={openModal}
                    mutateSearchAppointments={mutateSearchAppointments}
                  />
                )}
                {appointmentFilters.tableView === "OVERVIEW" && (
                  <WeekCalendar
                    items={items}
                    outlook={outlookEvents ?? []}
                    schedules={filteredSchedules}
                    openModal={openModal}
                    appointmentFilters={appointmentFilters}
                  />
                )}
              </>
            )}
          </SectionContainer>
        </PaddedContainer>
        <EnabledServicesModal
          setIsModalOpen={actions.showAvailableServices}
          isOpen={appointmentFilters.showAvailableServices}
          selectedTeam={selectTeam(appointmentFilters)}
        />
        {scheduledItem && (
          <ScheduleActivityModal
            isOpen={true}
            item={scheduledItem}
            closeModal={() => setScheduledItem(undefined)}
          />
        )}
        {isFollowUpEnabled && followUpData && (
          <BookFollowUpModal
            isOpen={Boolean(followUpSlot)}
            originalAppointment={followUpData.appointment}
            followUpAppointmentId={followUpSlot?.followUpAppointmentId}
            customer={followUpData.customer}
            startTime={followUpSlot?.startTime}
            endTime={followUpSlot?.endTime}
            selectedCoworker={followUpSlot?.selectedCoworker}
            selectedTeam={selectTeam(appointmentFilters)}
            projectId={
              followUpData.appointment.opportunityId ??
              followUpProjectContext?.projectId
            }
            onConfirm={() => {
              cancelFollowUpFlow();
            }}
            onCancelFlow={() => {
              setFollowUpSlot(undefined);
            }}
          />
        )}
      </CalendarProvider>
    </CalendarPageContainer>
  );
};
